import React, { useState } from 'react';
import { ShieldCheck, Copy, X, Loader2, Save, Trash2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function Admin2FA() {
  const { user, refresh } = useAuth();
  const [setupData, setSetupData] = useState(null); // {secret, qr_code}
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/2fa/setup');
      setSetupData(data);
    } catch { toast.error('Errore'); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/auth/2fa/enable', { code: code.trim() });
      setBackupCodes(data.backup_codes);
      setCode('');
      setSetupData(null);
      toast.success('2FA attivata');
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Codice non valido'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post('/auth/2fa/disable');
      toast.success('2FA disattivata');
      setShowConfirmDisable(false);
      await refresh();
    } catch { toast.error('Errore'); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Codici copiati');
  };

  if (user?.totp_enabled) {
    return (
      <div className="card-surface p-4 space-y-3" data-testid="admin-2fa-section">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--success)]/20 border border-[var(--success)]/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[var(--success)]" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-medium">2FA attiva</div>
            <div className="text-xs text-white/60">Il tuo account è protetto con autenticazione a due fattori.</div>
          </div>
        </div>
        <button
          data-testid="2fa-disable-btn"
          onClick={() => setShowConfirmDisable(true)}
          className="w-full py-2.5 rounded-2xl text-xs text-red-400/90 border border-red-500/30 hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 className="w-3.5 h-3.5" /> Disattiva 2FA
        </button>
        <AnimatePresence>
          {showConfirmDisable && (
            <Overlay onClose={() => setShowConfirmDisable(false)}>
              <h3 className="font-heading text-lg">Disattivare 2FA?</h3>
              <p className="text-sm text-white/70">Perderai un livello di protezione. Confermare?</p>
              <div className="flex gap-2">
                <button onClick={() => setShowConfirmDisable(false)} className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-sm">Annulla</button>
                <button onClick={disable} disabled={busy} className="flex-1 py-3 rounded-full bg-red-500 text-white text-sm font-semibold">Disattiva</button>
              </div>
            </Overlay>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="card-surface p-4 space-y-3" data-testid="admin-2fa-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[var(--gold-soft)] border border-[var(--gold)]/40 flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-[var(--gold)]" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-medium">Autenticazione a due fattori</div>
          <div className="text-xs text-white/60">Aumenta la sicurezza del tuo account admin con Google/Microsoft Authenticator o Authy.</div>
        </div>
      </div>
      {!setupData ? (
        <button
          data-testid="2fa-setup-btn"
          onClick={startSetup}
          disabled={busy}
          className="btn-gold w-full py-3 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Attiva 2FA</>}
        </button>
      ) : (
        <div className="space-y-3 pt-2">
          <div className="text-xs text-white/60">
            1. Apri Google Authenticator (o Authy/Microsoft) e scansiona il QR:
          </div>
          <div className="flex justify-center">
            <img src={setupData.qr_code} alt="QR" className="w-48 h-48 rounded-xl bg-white" data-testid="2fa-qr" />
          </div>
          <div className="text-[10px] text-white/40 text-center break-all">
            Chiave manuale: <span className="text-[var(--gold)]">{setupData.secret}</span>
          </div>
          <div className="text-xs text-white/60">2. Inserisci il codice a 6 cifre generato:</div>
          <input
            data-testid="2fa-verify-code" type="text" inputMode="numeric"
            className="input-gold text-center tracking-[0.4em] text-lg"
            placeholder="123456" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <button data-testid="2fa-enable-btn" onClick={enable} disabled={busy || code.length !== 6} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Conferma e attiva</>}
          </button>
        </div>
      )}

      <AnimatePresence>
        {backupCodes && (
          <Overlay onClose={() => setBackupCodes(null)}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg">Codici di recupero</h3>
              <button onClick={() => setBackupCodes(null)} className="text-white/50 p-1"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-white/60">Salva questi codici in un luogo sicuro. Ognuno può essere usato una sola volta se perdi accesso all'app authenticator.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {backupCodes.map((c) => (
                <div key={c} className="bg-[var(--bg-elevated)] rounded-lg py-2 text-center text-[var(--gold)]">{c}</div>
              ))}
            </div>
            <button onClick={copyCodes} className="w-full py-2.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] text-xs flex items-center justify-center gap-2">
              <Copy className="w-3.5 h-3.5" /> Copia tutti
            </button>
          </Overlay>
        )}
      </AnimatePresence>
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 40, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className="card-surface p-5 w-full max-w-sm space-y-4"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
